import styled from 'styled-components';

// ---------------------------------------------------------------------------
// Casca da tela "Gerenciar perfil de acesso" (redesenho 2026-09-15, handoff
// docs/design/design_handoff_permissoes_perfil).
//
// A paleta abaixo é de DOMÍNIO desta tela (semáforo de sensibilidade e
// estados da matriz) — não são cores de ação de botão (essas vêm do kit
// FC7). Âmbar = permissão sensível; azul de preenchimento = concedida
// (deriva do actionWrite do tema); cinza = negada.
// ---------------------------------------------------------------------------

export const AMBER = {
   base: '#e88c14',
   border: '#c9781a',
   text: '#8a5000',
   deepText: '#5c3800',
   bgSoft: 'rgba(232, 140, 20, 0.08)',
   bgPill: 'rgba(232, 140, 20, 0.13)',
   bgChip: 'rgba(232, 140, 20, 0.14)',
   borderSoft: 'rgba(232, 140, 20, 0.28)',
   cellBorder: 'rgba(232, 140, 20, 0.55)',
};

export const BLUE = {
   text: '#1f66bd',
   bgHover: 'rgba(64, 152, 239, 0.06)',
   bgRailHover: 'rgba(64, 152, 239, 0.08)',
   bgPill: 'rgba(64, 152, 239, 0.12)',
   bgActive: 'rgba(64, 152, 239, 0.14)',
};

export const GREEN = {
   text: '#2a7d4f',
   bgPill: 'rgba(63, 158, 107, 0.14)',
   bgTag: 'rgba(63, 158, 107, 0.16)',
};

export const RED = {
   text: '#a52b2b',
   bgTag: 'rgba(207, 58, 58, 0.14)',
};

export const GRAY_DENIED = '#b9b9b9';

// ── Shell da página ─────────────────────────────────────────────────────────

/* Teto de 1200px (ajuste do Caio, 2026-09-15 — o hifi pedia 1440, mas sem
   contar a sidebar do app aberta): o 1fr da matriz preenche o card inteiro e
   a sobra vai toda pra coluna Recurso/Tela, empurrando as células de ação
   pra longe do nome. Em 1200 a coluna fica ~570px e a leitura fecha; abaixo
   de 1020 o card rola horizontal (min-width do grid, inalterado). */
export const Wrap = styled.div`
   max-width: 1200px;
   margin: 0 auto;
   display: flex;
   flex-direction: column;
   gap: 14px;
   /* reserva para a barra de salvamento fixa */
   padding-bottom: 96px;
`;

export const DarkTitle = styled.h1`
   font-size: 22px;
   font-weight: 800;
   letter-spacing: -0.01em;
   color: #fff;
   margin: 0;
`;

/* ⚠️ H.4.1 não se aplica aqui: o recorte é intencional e a tela NÃO tem
   autocomplete com dropdown dentro do card — o overflow:auto é a única
   rolagem horizontal da tela (abaixo de ~1270px o card rola em vez de
   comprimir a coluna de recursos). Se um dia entrar um autocomplete,
   repensar o recorte. */
export const Card = styled.div`
   background: ${({ theme }) => theme.colors.surface};
   border: 1px solid ${({ theme }) => theme.colors.border};
   border-radius: 12px;
   box-shadow: ${({ theme }) => theme.shadows.sm};
   overflow: auto;
`;

// ── Cabeçalho do perfil (dentro do card) ────────────────────────────────────

export const ProfileHead = styled.div`
   padding: 16px 18px 0;
   display: flex;
   align-items: flex-start;
   justify-content: space-between;
   gap: 24px;
`;

export const ProfileHeadLeft = styled.div`
   max-width: 720px;
`;

export const ProfileNameRow = styled.div`
   display: flex;
   align-items: center;
   gap: 10px;
`;

export const ProfileName = styled.h2`
   font-size: 18px;
   font-weight: 800;
   letter-spacing: -0.01em;
   color: ${({ theme }) => theme.colors.textPrimary};
   margin: 0;
`;

export const IdPill = styled.span`
   display: inline-flex;
   align-items: center;
   height: 22px;
   padding: 0 9px;
   border-radius: 999px;
   background: #f2f2f2;
   border: 1px solid ${({ theme }) => theme.colors.border};
   font-size: 11px;
   font-weight: 700;
   color: ${({ theme }) => theme.colors.textMuted};
   font-variant-numeric: tabular-nums;
   white-space: nowrap;
`;

export const ProfileDescription = styled.p`
   margin: 6px 0 0;
   font-size: 12.5px;
   color: ${({ theme }) => theme.colors.textMuted};
   text-wrap: pretty;
`;

export const ProfileMetaLine = styled.div`
   font-size: 12.5px;
   color: #777;
   text-align: right;
   white-space: nowrap;
   padding-top: 4px;
`;

// ── Abas ────────────────────────────────────────────────────────────────────

export const TabsRow = styled.div`
   display: flex;
   gap: 2px;
   padding: 12px 18px 0;
   border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

// ── Toolbar de busca ────────────────────────────────────────────────────────

export const SearchToolbar = styled.div`
   display: flex;
   align-items: center;
   gap: 14px;
   padding: 12px 16px;
   border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

export const SearchBox = styled.div`
   position: relative;
   flex: 1 1 280px;
   min-width: 220px;
   max-width: 420px;

   svg {
      position: absolute;
      left: 11px;
      top: 11px;
      width: 18px;
      height: 18px;
      color: #8a8a8a;
      pointer-events: none;
   }
`;

export const SearchInput = styled.input`
   width: 100%;
   height: 40px;
   border-radius: 10px;
   border: 1px solid rgba(0, 0, 0, 0.14);
   font-size: 13px;
   padding: 0 12px 0 34px;
   background: ${({ theme }) => theme.colors.surface};

   &:focus {
      outline: none;
      border-color: ${({ theme }) => theme.colors.actionWrite};
      box-shadow: 0 0 0 3px rgba(64, 152, 239, 0.18);
   }
`;

export const ToolbarMeta = styled.div`
   font-size: 12.5px;
   color: ${({ theme }) => theme.colors.textMuted};
   white-space: nowrap;
`;

export const ToolbarSpacer = styled.div`
   flex: 1;
`;

// Gatilho do modo curadoria de sensibilidade (contrato 0111). Controle local
// de domínio (chip com estado), não variante do kit — e por isso declara o
// próprio :hover com background (armadilha G.4.1).
export const CurationToggle = styled.button`
   display: inline-flex;
   align-items: center;
   gap: 7px;
   height: 40px;
   padding: 0 14px;
   border-radius: 10px;
   font-size: 13px;
   font-weight: 700;
   font-family: inherit;
   cursor: pointer;
   appearance: none;
   border: 1px solid
      ${({ $active }) => ($active ? AMBER.border : 'rgba(0, 0, 0, 0.14)')};
   background: ${({ $active }) => ($active ? AMBER.bgChip : '#fff')};
   color: ${({ $active, theme }) =>
      $active ? AMBER.text : theme.colors.textMuted};
   white-space: nowrap;

   &:hover:not(:disabled) {
      background: ${({ $active }) => ($active ? AMBER.bgChip : AMBER.bgSoft)};
      border-color: ${AMBER.border};
      color: ${AMBER.text};
   }
   &:focus-visible {
      outline: none;
      box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.focusRing};
   }
`;

export const CurationBanner = styled.div`
   display: flex;
   align-items: center;
   gap: 8px;
   padding: 8px 16px;
   background: ${AMBER.bgSoft};
   border-bottom: 1px solid ${AMBER.borderSoft};
   font-size: 12px;
   font-weight: 600;
   color: ${AMBER.text};
`;

// ── Grid de 2 colunas da aba Permissões (v2 do handoff, 2026-09-15) ─────────
// A coluna direita de resumo (288px) morreu: o resumo subiu pro topo do rail
// e o diff virou modal — min-width caiu de 1270px pra 1020px.

export const PermissionsLayout = styled.div`
   display: grid;
   grid-template-columns: 300px minmax(700px, 1fr);
   min-width: 1020px;
   align-items: stretch;
`;

// Coluna A inteira (resumo + módulos) — o fundo/borda moram aqui para os dois
// blocos internos herdarem.
export const RailColumn = styled.div`
   background: #fafafa;
   border-right: 1px solid ${({ theme }) => theme.colors.border};
   display: flex;
   flex-direction: column;
`;

// Pill contador do diff ("+X −Y") — compartilhada entre o badge do rail e o
// header do modal de alterações.
export const DiffCountPill = styled.span`
   display: inline-flex;
   align-items: center;
   padding: 1px 8px;
   border-radius: 999px;
   font-size: 11.5px;
   font-weight: 800;
   font-variant-numeric: tabular-nums;
   white-space: nowrap;
   background: ${({ $active }) => ($active ? AMBER.bgChip : '#f2f2f2')};
   color: ${({ $active }) => ($active ? AMBER.text : '#8a8a8a')};
`;

// ── Pills compartilhadas entre matriz e resumo ──────────────────────────────

export const SensitivePill = styled.span`
   display: inline-flex;
   align-items: center;
   gap: 4px;
   height: 19px;
   padding: 0 7px;
   border-radius: 999px;
   font-size: 10.5px;
   font-weight: 800;
   background: ${AMBER.bgPill};
   color: ${AMBER.text};

   svg {
      width: 14px;
      height: 14px;
   }
`;

export const ChangedPill = styled.span`
   display: inline-flex;
   align-items: center;
   height: 19px;
   padding: 0 7px;
   border-radius: 999px;
   font-size: 10.5px;
   font-weight: 800;
   background: ${BLUE.bgActive};
   color: ${BLUE.text};
`;

export const SectionLabel = styled.div`
   font-size: 11px;
   font-weight: 800;
   letter-spacing: 0.06em;
   text-transform: uppercase;
   color: #8a8a8a;
`;
